/**
 * Custom horizontal scroll indicator for product specs tables.
 * Targets only `.details-specs__content .table-outer`.
 */
(function () {
  'use strict';

  var TABLE_SELECTOR = '.details-specs__content .table-outer';
  var INIT_ATTR = 'data-scroll-indicator-init';
  var MIN_THUMB_WIDTH = 48;
  var instances = [];

  function ensureIndicatorElements(tableOuter) {
    var indicator = tableOuter.querySelector(':scope > .scroll-indicator');

    if (
      !indicator &&
      tableOuter.nextElementSibling &&
      tableOuter.nextElementSibling.classList.contains('scroll-indicator')
    ) {
      indicator = tableOuter.nextElementSibling;
    }

    if (!indicator) {
      indicator = document.createElement('div');
      indicator.className = 'scroll-indicator';
      indicator.setAttribute('role', 'scrollbar');
      indicator.setAttribute('aria-orientation', 'horizontal');

      if (tableOuter.nextSibling) {
        tableOuter.parentNode.insertBefore(indicator, tableOuter.nextSibling);
      } else {
        tableOuter.parentNode.appendChild(indicator);
      }
    } else if (indicator.parentNode === tableOuter) {
      if (tableOuter.nextSibling) {
        tableOuter.parentNode.insertBefore(indicator, tableOuter.nextSibling);
      } else {
        tableOuter.parentNode.appendChild(indicator);
      }
    }

    indicator.setAttribute('role', 'scrollbar');
    indicator.setAttribute('aria-orientation', 'horizontal');

    var track = indicator.querySelector('.scroll-indicator__track');
    if (!track) {
      track = document.createElement('div');
      track.className = 'scroll-indicator__track';
      indicator.appendChild(track);
    }

    var thumb = track.querySelector('.scroll-indicator__thumb');
    if (!thumb) {
      thumb = document.createElement('div');
      thumb.className = 'scroll-indicator__thumb';
      track.appendChild(thumb);
    }

    return {
      indicator: indicator,
      track: track,
      thumb: thumb
    };
  }

  function createScrollIndicator(tableOuter) {
    if (tableOuter.getAttribute(INIT_ATTR) === 'true') {
      return null;
    }

    if (!tableOuter.querySelector('.specs-table, table')) {
      return null;
    }

    tableOuter.setAttribute(INIT_ATTR, 'true');

    var elements = ensureIndicatorElements(tableOuter);
    var indicator = elements.indicator;
    var track = elements.track;
    var thumb = elements.thumb;

    var state = {
      tableOuter: tableOuter,
      indicator: indicator,
      track: track,
      thumb: thumb,
      isDragging: false,
      dragStartX: 0,
      dragStartThumbLeft: 0,
      rafId: null
    };

    function getTrackWidth() {
      var trackWidth = track.getBoundingClientRect().width;

      if (trackWidth <= 0) {
        trackWidth = indicator.getBoundingClientRect().width;
      }

      if (trackWidth <= 0) {
        trackWidth = tableOuter.getBoundingClientRect().width;
      }

      return trackWidth;
    }

    function getMetrics() {
      var scrollWidth = tableOuter.scrollWidth;
      var clientWidth = tableOuter.clientWidth;
      var scrollLeft = tableOuter.scrollLeft;
      var trackWidth = getTrackWidth();
      var maxScroll = Math.max(scrollWidth - clientWidth, 0);
      var ratio = scrollWidth > 0 ? clientWidth / scrollWidth : 1;
      var thumbWidth = Math.max(ratio * trackWidth, MIN_THUMB_WIDTH);

      if (thumbWidth > trackWidth) {
        thumbWidth = trackWidth;
      }

      var maxThumbOffset = Math.max(trackWidth - thumbWidth, 0);
      var thumbLeft = maxScroll > 0 ? (scrollLeft / maxScroll) * maxThumbOffset : 0;

      return {
        scrollWidth: scrollWidth,
        clientWidth: clientWidth,
        scrollLeft: scrollLeft,
        trackWidth: trackWidth,
        maxScroll: maxScroll,
        thumbWidth: thumbWidth,
        maxThumbOffset: maxThumbOffset,
        thumbLeft: thumbLeft,
        hasOverflow: maxScroll > 1
      };
    }

    function applyThumb(metrics) {
      thumb.style.width = metrics.thumbWidth + 'px';
      thumb.style.transform = 'translate3d(' + metrics.thumbLeft + 'px, 0, 0)';
    }

    function updateIndicator() {
      var metrics = getMetrics();

      if (!metrics.hasOverflow || metrics.trackWidth <= 0) {
        indicator.classList.add('scroll-indicator--hidden');
        indicator.setAttribute('aria-hidden', 'true');
        return;
      }

      indicator.classList.remove('scroll-indicator--hidden');
      indicator.setAttribute('aria-hidden', 'false');
      indicator.setAttribute('aria-valuemin', '0');
      indicator.setAttribute('aria-valuemax', String(Math.round(metrics.maxScroll)));
      indicator.setAttribute('aria-valuenow', String(Math.round(metrics.scrollLeft)));

      applyThumb(metrics);
    }

    function scheduleUpdate() {
      if (state.rafId !== null) {
        return;
      }

      state.rafId = window.requestAnimationFrame(function () {
        state.rafId = null;
        updateIndicator();
      });
    }

    function scrollFromThumbLeft(thumbLeft, metrics) {
      if (!metrics) {
        metrics = getMetrics();
      }

      if (metrics.maxThumbOffset <= 0) {
        return;
      }

      var clampedLeft = Math.min(Math.max(thumbLeft, 0), metrics.maxThumbOffset);
      var scrollRatio = clampedLeft / metrics.maxThumbOffset;
      tableOuter.scrollLeft = scrollRatio * metrics.maxScroll;
      applyThumb({
        thumbWidth: metrics.thumbWidth,
        thumbLeft: clampedLeft
      });
    }

    function scrollFromTrackPointer(clientX) {
      var metrics = getMetrics();
      if (!metrics.hasOverflow) {
        return;
      }

      var trackRect = track.getBoundingClientRect();
      var pointerOffset = clientX - trackRect.left;
      var targetThumbLeft = pointerOffset - metrics.thumbWidth / 2;
      scrollFromThumbLeft(targetThumbLeft, metrics);
      scheduleUpdate();
    }

    function onTableScroll() {
      if (state.isDragging) {
        return;
      }
      scheduleUpdate();
    }

    function onPointerDown(event) {
      if (event.button !== undefined && event.button !== 0) {
        return;
      }

      state.isDragging = true;
      thumb.classList.add('is-dragging');

      var metrics = getMetrics();
      state.dragStartX = getPointerX(event);
      state.dragStartThumbLeft = metrics.thumbLeft;

      document.addEventListener('mousemove', onPointerMove);
      document.addEventListener('mouseup', onPointerUp);
      document.addEventListener('touchmove', onPointerMove, { passive: false });
      document.addEventListener('touchend', onPointerUp);
      document.addEventListener('touchcancel', onPointerUp);

      event.preventDefault();
    }

    function onPointerMove(event) {
      if (!state.isDragging) {
        return;
      }

      var metrics = getMetrics();
      var deltaX = getPointerX(event) - state.dragStartX;
      scrollFromThumbLeft(state.dragStartThumbLeft + deltaX, metrics);

      if (event.cancelable) {
        event.preventDefault();
      }
    }

    function onPointerUp() {
      if (!state.isDragging) {
        return;
      }

      state.isDragging = false;
      thumb.classList.remove('is-dragging');

      document.removeEventListener('mousemove', onPointerMove);
      document.removeEventListener('mouseup', onPointerUp);
      document.removeEventListener('touchmove', onPointerMove);
      document.removeEventListener('touchend', onPointerUp);
      document.removeEventListener('touchcancel', onPointerUp);

      scheduleUpdate();
    }

    function onTrackClick(event) {
      if (event.target === thumb) {
        return;
      }
      scrollFromTrackPointer(getPointerX(event));
    }

    function getPointerX(event) {
      if (event.touches && event.touches.length) {
        return event.touches[0].clientX;
      }
      if (event.changedTouches && event.changedTouches.length) {
        return event.changedTouches[0].clientX;
      }
      return event.clientX;
    }

    tableOuter.addEventListener('scroll', onTableScroll, { passive: true });
    thumb.addEventListener('mousedown', onPointerDown);
    thumb.addEventListener('touchstart', onPointerDown, { passive: false });
    track.addEventListener('click', onTrackClick);

    var resizeObserver = null;
    if (typeof ResizeObserver !== 'undefined') {
      resizeObserver = new ResizeObserver(scheduleUpdate);
      resizeObserver.observe(tableOuter);
      resizeObserver.observe(indicator);
      resizeObserver.observe(track);
    }

    var mutationObserver = new MutationObserver(scheduleUpdate);
    mutationObserver.observe(tableOuter, {
      childList: true,
      subtree: true,
      characterData: true,
      attributes: true
    });

    window.addEventListener('resize', scheduleUpdate, { passive: true });
    window.addEventListener('load', scheduleUpdate, { passive: true });

    updateIndicator();
    setTimeout(updateIndicator, 50);
    setTimeout(updateIndicator, 300);

    return {
      destroy: function () {
        if (state.rafId !== null) {
          window.cancelAnimationFrame(state.rafId);
        }
        tableOuter.removeEventListener('scroll', onTableScroll);
        thumb.removeEventListener('mousedown', onPointerDown);
        thumb.removeEventListener('touchstart', onPointerDown);
        track.removeEventListener('click', onTrackClick);
        window.removeEventListener('resize', scheduleUpdate);
        window.removeEventListener('load', scheduleUpdate);
        mutationObserver.disconnect();
        if (resizeObserver) {
          resizeObserver.disconnect();
        }
        tableOuter.removeAttribute(INIT_ATTR);
      },
      refresh: updateIndicator
    };
  }

  function initAll() {
    var tables = document.querySelectorAll(TABLE_SELECTOR);

    for (var i = 0; i < tables.length; i++) {
      var instance = createScrollIndicator(tables[i]);
      if (instance) {
        instances.push(instance);
      }
    }
  }

  function observeNewTables() {
    if (typeof MutationObserver === 'undefined') {
      return;
    }

    var rootObserver = new MutationObserver(function (mutations) {
      var shouldInit = false;

      for (var i = 0; i < mutations.length; i++) {
        var mutation = mutations[i];
        if (mutation.type !== 'childList') {
          continue;
        }

        for (var j = 0; j < mutation.addedNodes.length; j++) {
          var node = mutation.addedNodes[j];
          if (node.nodeType !== 1) {
            continue;
          }

          if (node.matches && node.matches(TABLE_SELECTOR)) {
            shouldInit = true;
            break;
          }

          if (node.querySelector && node.querySelector(TABLE_SELECTOR)) {
            shouldInit = true;
            break;
          }
        }

        if (shouldInit) {
          break;
        }
      }

      if (shouldInit) {
        initAll();
      }
    });

    rootObserver.observe(document.body, {
      childList: true,
      subtree: true
    });
  }

  function bootstrap() {
    initAll();
    observeNewTables();
    window.addEventListener('load', initAll);
    setTimeout(initAll, 100);
    setTimeout(initAll, 500);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', bootstrap);
  } else {
    bootstrap();
  }

  window.DetailsSpecsScrollIndicator = {
    init: initAll,
    getInstances: function () {
      return instances.slice();
    }
  };
})();
